import { useState } from 'react';

import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback';
import { processError } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor';

import type { ApolloErrorInterop } from '../entry-points/ApolloErrorInterop';

type MutationFunctionOptions<TData, TVariables> = {
	variables?: TVariables;
	update?: (cache: null, result: { data?: TData | null }) => void;
	optimisticResponse?: TData | ((vars: TVariables) => TData);
};

type MutationResult<TData> = {
	data: TData | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	called: boolean;
};

type UseApolloCompatibleMutationBaseParams<TData, TVariables> = {
	fetchData: (variables?: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void | Promise<void>;
	onError?: (error: ApolloErrorInterop) => void;
	update?: (cache: null, result: { data?: TData | null }) => void;
	optimisticResponse?: TData | ((vars: TVariables) => TData);
	convertToApolloError: (error: Error) => Promise<ApolloErrorInterop>;
};

export function useApolloCompatibleMutation<TData, TVariables>({
	fetchData,
	onCompleted,
	onError,
	update: defaultUpdate,
	optimisticResponse: defaultOptimisticResponse,
	convertToApolloError,
}: UseApolloCompatibleMutationBaseParams<TData, TVariables>): [
	(options?: MutationFunctionOptions<TData, TVariables>) => Promise<{ data: TData | undefined }>,
	MutationResult<TData>,
] {
	const [result, setResult] = useState<MutationResult<TData>>({
		data: undefined,
		loading: false,
		error: undefined,
		called: false,
	});

	const mutate = useStableCallback(
		async (options: MutationFunctionOptions<TData, TVariables> = {}) => {
			const {
				variables,
				update: optionsUpdate,
				optimisticResponse: optionsOptimisticResponse,
			} = options;
			const update = optionsUpdate ?? defaultUpdate;

			setResult({ data: undefined, loading: true, error: undefined, called: true });

			const optimisticResponse = optionsOptimisticResponse ?? defaultOptimisticResponse;

			if (optimisticResponse) {
				const optimisticData =
					typeof optimisticResponse === 'function'
						? (optimisticResponse as (vars: TVariables) => TData)(variables ?? ({} as TVariables))
						: optimisticResponse;

				update?.(null, { data: optimisticData });
			}

			try {
				const data = await fetchData(variables);
				// The `update` function matches the Apollo useMutation API for compatibility
				// This hook does not implement a cache, so `cache` is always null
				update?.(null, { data });

				const newResult = { data, loading: false, error: undefined, called: true };
				setResult(newResult);

				await onCompleted?.(data);

				return { data: newResult.data };
			} catch (error) {
				// Rollback optimistic update on error
				if (optimisticResponse && update) {
					// Signal rollback by calling update with undefined data
					// Consumers should handle this to revert to the previous state
					update(null, { data: undefined });
				}

				const apolloError = await Promise.resolve(convertToApolloError(error));
				if (apolloError.graphQLErrors?.length) {
					apolloError.graphQLErrors.forEach((e) => {
						processError(e);
					});
				}
				onError?.(apolloError);

				const newResult = { data: undefined, loading: false, error: apolloError, called: true };
				setResult(newResult);

				if (!onError) {
					throw apolloError;
				}
				return { data: newResult.data };
			}
		},
	);

	return [mutate, result];
}
